import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { ORG_SECTIONS, orgPath, orgSectionBySlug, orgSectionByTab, visibleOrgSections } from "../lib/collaborators";
import useCollabReferentials from "../hooks/useCollabReferentials";
import useSkillsReferentials from "../hooks/useSkillsReferentials";
import { JobFunctionsPanel, LevelsPanel, SkillsPanel } from "../components/SkillsReferentialPanels";
import MissionTypesPanel from "../components/missions/MissionTypesPanel";
import ReportTemplatesPanel from "../components/missions/ReportTemplatesPanel";
import ServicesPanel from "../components/organisation/ServicesPanel";
import ReferentialPanel from "../components/organisation/ReferentialPanel";
import ConsistencyPanel from "../components/organisation/ConsistencyPanel";
import MandateTypesPanel from "../components/organisation/MandateTypesPanel";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { AlertCircle } from "lucide-react";

// One section of the « Collaborateurs » application's Organisation menu: the address names it, the panels live in components/organisation.
export default function Organisation() {
  const { user } = useAuth();
  const refs = useCollabReferentials();
  const canManage = hasPermission(user, "services:manage");
  const skillRefs = useSkillsReferentials();
  const canManageSkills = hasPermission(user, "skills:manage");
  const canManageMissionTypes = hasPermission(user, "missions:manage_types");
  const { section } = useParams();
  const [searchParams] = useSearchParams();
  const allowed = visibleOrgSections(user, (key) => hasPermission(user, key));
  const current = orgSectionBySlug(section);
  // A bare address (or an old `?tab=` link) opens the right section; a section the person may not open falls back to the first one.
  const target = current && allowed.includes(current) ? null : (orgSectionByTab(searchParams.get("tab")) || ORG_SECTIONS[0]);

  if (target) return <Navigate to={allowed.includes(target) ? orgPath(target.slug) : orgPath(allowed[0].slug)} replace />;
  if (refs.status === "loading") {
    return <div className="space-y-3" data-testid="org-loading"><Skeleton className="h-8 w-56" /><Skeleton className="h-48 w-full" /></div>;
  }
  if (refs.status === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="org-error">
        <AlertCircle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">Impossible de charger l'organisation. Réessayez dans quelques instants.</p>
        <Button variant="outline" onClick={refs.reload}>Réessayer</Button>
      </Card>
    );
  }

  const tab = current.tab;

  return (
    <div className="space-y-4" data-testid="org-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900" data-testid="org-title">{current.label}</h1>
        <p className="text-sm text-slate-500">{current.description}</p>
      </div>
      {tab === "services" && <ServicesPanel refs={refs} canManage={canManage} />}
      {tab === "zone" && <ReferentialPanel kind="zone" items={refs.zones} refs={refs} canManage={canManage} />}
      {tab === "relation_type" && <ReferentialPanel kind="relation_type" items={refs.relation_types} refs={refs} canManage={canManage} />}
      {tab === "job_functions" && <JobFunctionsPanel refs={skillRefs} canManage={canManageSkills} />}
      {tab === "skills" && <SkillsPanel refs={skillRefs} canManage={canManageSkills} />}
      {tab === "levels" && <LevelsPanel refs={skillRefs} canManage={canManageSkills} />}
      {tab === "mission_types" && <MissionTypesPanel skillRefs={skillRefs} canManage={canManageMissionTypes} />}
      {tab === "report_templates" && <ReportTemplatesPanel />}
      {tab === "mandate_types" && <MandateTypesPanel />}
      {tab === "consistency" && <ConsistencyPanel />}
    </div>
  );
}
